/**
 * planningNavConfig.ts — nav config for the Planning app mobile shell.
 *
 * Mirrors the desktop PlanningSidebar's operating-domain vocabulary, compressed
 * for the thumb, so the two surfaces teach the same map:
 *
 *   Desktop section     → Mobile home
 *   ─────────────────────────────────────────────────────────────
 *   Commitment          → Home + Plans (bottom-bar tabs)
 *   Capabilities        → Viability + Launch (bottom-bar tabs)
 *   Plan operations     → "Plan operations" drawer section
 *   People & structure  → "People & structure" drawer section
 *   Settings            → "Workspace" drawer section
 *
 * The Viability/Launch tabs link straight to the engine hub + launch calendar —
 * those pages already render their own inputs/results navigation, so
 * re-exposing every engine leaf in a bottom-sheet
 * submenu would just duplicate on-page navigation.
 */
import {
  Home,
  ClipboardList,
  GitBranch,
  Users,
  Menu,
  Calendar,
  Rocket,
  AlertTriangle,
  GitCompare,
  Shield,
  Box,
  Search,
  Target,
  Settings,
  TableProperties,
} from 'lucide-react';
import { MORE_SENTINEL, SEARCH_SENTINEL, type MobileNavItem, type MobileNavSection } from './navConfig';
import { resolveEngineTitle, ENGINE_BASE } from './mobileEngineRoutes';
import { resolvePlanningModuleTitle, PLANS_BASE, PLANNING_BASE } from './mobilePlanningRoutes';
import {
  HELP_MENU,
  SHARED_BOTTOM,
  planSectionLinks,
  type AreaMenuItem,
} from '../lib/areaMenus';

const LAUNCH_CALENDAR_HREF = '/planning/launch-calendar';

/**
 * Launch-specific engine inputs live under /planning/engine/* but belong to the
 * Launch capability (matches desktop's isLaunchPlanningPath).
 */
const LAUNCH_ENGINE_SUBS = ['participation', 'time-phased', 'funding'];

function isLaunchPath(pathname: string): boolean {
  if (pathname.startsWith(LAUNCH_CALENDAR_HREF)) return true;
  return LAUNCH_ENGINE_SUBS.some((sub) => pathname.startsWith(`${ENGINE_BASE}/${sub}`));
}

/**
 * Bottom-bar tabs (Launch is dropped by usePlanningMobileNav when time-phased
 * planning is off, leaving Home · Plans · Viability · More).
 */
export const PLANNING_TAB_ITEMS: MobileNavItem[] = [
  { label: 'Home', href: PLANNING_BASE, icon: Home },
  // Commitment — the plan of record / plan cycles list.
  { label: 'Plans', href: PLANS_BASE, icon: ClipboardList },
  // Capabilities — the feasibility engine (its hub page owns inputs/results nav).
  { label: 'Viability', href: ENGINE_BASE, icon: Target },
  { label: 'Launch', href: LAUNCH_CALENDAR_HREF, icon: Rocket },
  { label: 'More', href: MORE_SENTINEL, icon: Menu },
];

export const PLANNING_DRAWER_SECTIONS: MobileNavSection[] = [
  {
    label: 'Plan operations',
    items: [
      { label: 'Governance', href: '/planning/governance', icon: Shield },
      { label: 'Assumptions & Risks', href: '/planning/assumptions-risks', icon: AlertTriangle },
      { label: 'Changes', href: '/planning/changes', icon: GitCompare },
      { label: 'Work Graph', href: '/planning/work-graph', icon: GitBranch },
      { label: 'Plan Workbench', href: '/planning/spreadsheet', icon: TableProperties },
    ],
  },
  {
    label: 'People & structure',
    items: [
      { label: 'People & Capacity', href: '/planning/people-capacity', icon: Users },
      { label: 'Areas', href: '/planning/areas', icon: Box },
      { label: 'Milestones', href: '/planning/milestones', icon: Calendar },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { label: 'Search', href: SEARCH_SENTINEL, icon: Search },
      { label: 'Settings', href: '/prioritization/settings', icon: Settings },
    ],
  },
];

const toMobileItem = (item: AreaMenuItem): MobileNavItem => ({ label: item.name, href: item.href, icon: item.icon });

/**
 * What the desktop Plans sidebar draws besides its own menu (audit 2026-11-02,
 * #39), from the same lists it draws them from (src/lib/areaMenus.ts):
 *  - `plan`: the open plan's own links, when a plan is open (planSectionLinks);
 *  - `tail`: Reports (the area's reports, useAreaReports) and Decisions — both
 *    under the Reports module, as on the desktop — then Help.
 * `reports` is null when the Reports module is off. `allowed` is the role-based
 * menu's check (a plan link follows its `gate`); Help is for everyone.
 */
export function planningDrawerExtras({
  planId,
  reports,
  allowed = () => true,
}: {
  planId: string | null;
  reports: AreaMenuItem[] | null;
  allowed?: (href: string) => boolean;
}): { plan: MobileNavSection | null; tail: MobileNavSection[] } {
  const planItems = planId ? planSectionLinks(planId).filter((link) => allowed(link.gate)).map(toMobileItem) : [];
  const tail: MobileNavSection[] = [];
  if (reports) {
    const reportItems = reports.filter((item) => allowed(item.href)).map(toMobileItem);
    const decisionItems = SHARED_BOTTOM.filter((item) => item.href !== '/prioritization/reports' && allowed(item.href)).map(toMobileItem);
    if (reportItems.length > 0) tail.push({ label: 'Reports', items: reportItems });
    if (decisionItems.length > 0) tail.push({ label: 'Decisions', items: decisionItems });
  }
  tail.push({ label: HELP_MENU.label, items: HELP_MENU.items.map(toMobileItem) });
  return { plan: planItems.length > 0 ? { label: 'This plan', items: planItems } : null, tail };
}

/** Which tab should appear active for a given planning path. */
export function activePlanningTabHref(pathname: string): string | null {
  // Launch owns the launch calendar + launch-specific engine inputs.
  if (isLaunchPath(pathname)) return LAUNCH_CALENDAR_HREF;

  // Everything else under the engine is Viability.
  if (pathname.startsWith(ENGINE_BASE)) return ENGINE_BASE;

  // Commitment — the plan list and any specific plan.
  if (pathname === PLANS_BASE || pathname.startsWith(`${PLANS_BASE}/`)) return PLANS_BASE;

  // Home (exact — deep drawer routes highlight no tab rather than mis-lighting Home).
  if (
    pathname === PLANNING_BASE ||
    pathname === `${PLANNING_BASE}/` ||
    pathname === `${PLANNING_BASE}/dashboard`
  ) {
    return PLANNING_BASE;
  }

  return null;
}

/** Best-effort title for a planning route. */
export function resolvePlanningTitle(pathname: string): string {
  const engineTitle = resolveEngineTitle(pathname);
  if (engineTitle) return engineTitle;

  const moduleTitle = resolvePlanningModuleTitle(pathname);
  if (moduleTitle) return moduleTitle;

  const all: MobileNavItem[] = PLANNING_TAB_ITEMS.filter((t) => t.href !== MORE_SENTINEL).concat(
    PLANNING_DRAWER_SECTIONS.flatMap((s) => s.items),
  );
  const match = all
    .filter(
      (i) =>
        !i.href.startsWith('__') &&
        (pathname === i.href ||
          pathname.startsWith(i.href + '/') ||
          pathname.startsWith(i.href + '?')),
    )
    .sort((a, b) => b.href.length - a.href.length)[0];
  if (match) return match.label;
  const seg = pathname.split('?')[0].split('/').filter(Boolean).pop() ?? 'Planning';
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
